import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchPtzState, ptzFocusFromRing } from "../../../api/ptz.ts";
import {
  callCameraPreset,
  fetchCameraPresets,
  type CameraPreset,
} from "../../../api/ptzPresets.ts";
import { useFlashbackPtzControl } from "../../../hooks/flashbackHooks/useFlashbackPtzControl.ts";
import { normalizeCameraVendor } from "../../../lib/cameraVendor.ts";
import { PRESET_GRID_TOTAL } from "../../../lib/presetGrid.ts";
import { adjustNormYForPtzAim } from "../../../lib/ptzPanoramaMap.ts";

export type PatrolLivePtzAimOptions = {
  /** `hikvision` | `seetong` from camera row / live patrol recording. */
  vendor?: unknown;
};

/**
 * Live PTZ + immersive 360° click (Patrol / wankhede-360).
 * Seetong: preset grid → `callCameraPreset`. Hikvision: absolute → `ptz/focus`.
 */
export function usePatrolLivePtzAim(
  cameraId: string,
  enabled: boolean,
  options: PatrolLivePtzAimOptions = {},
) {
  const control = useFlashbackPtzControl(cameraId, enabled, {
    vendor: options.vendor,
  });
  const ptzRef = useRef(control.ptz);
  const ptzLimitsRef = useRef(control.ptzLimits);
  const [cameraPresets, setCameraPresets] = useState<CameraPreset[]>([]);
  const [selectedPresetGridId, setSelectedPresetGridId] = useState<
    number | null
  >(null);

  const vendor = useMemo(
    () => normalizeCameraVendor(options.vendor),
    [options.vendor],
  );
  const usesPresetGridPanoramaPick = vendor === "seetong";

  const mappedPresetGridIds = useMemo(
    () =>
      [...new Set(cameraPresets.map((row) => row.grid_id))]
        .filter((id) => id >= 1 && id <= PRESET_GRID_TOTAL)
        .sort((a, b) => a - b),
    [cameraPresets],
  );

  useEffect(() => {
    ptzRef.current = control.ptz;
  }, [control.ptz]);

  useEffect(() => {
    ptzLimitsRef.current = control.ptzLimits;
  }, [control.ptzLimits]);

  useEffect(() => {
    setSelectedPresetGridId(null);
  }, [cameraId]);

  useEffect(() => {
    if (!enabled || !cameraId || !usesPresetGridPanoramaPick) {
      setCameraPresets([]);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchCameraPresets(cameraId);
        if (!cancelled) setCameraPresets(rows);
      } catch {
        if (!cancelled) setCameraPresets([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cameraId, enabled, usesPresetGridPanoramaPick]);

  const handlePresetGridSelect = useCallback(
    (gridId: number) => {
      if (!enabled || !cameraId || !control.ready) return;
      setSelectedPresetGridId(gridId);
      if (!cameraPresets.some((row) => row.grid_id === gridId)) {
        console.warn(`Patrol preset: no preset mapped for grid ${gridId}.`);
        return;
      }
      void callCameraPreset({ cameraid: cameraId, gridid: gridId })
        .then(() => fetchPtzState(cameraId))
        .then((s) => control.syncPtz(s))
        .catch((err) => {
          console.warn("Patrol PTZ preset move failed:", err);
        });
    },
    [cameraId, cameraPresets, control, enabled],
  );

  const applyPanoramaAimFromNorm = useCallback(
    (normX: number, normY: number) => {
      if (!enabled || !cameraId || !control.ready) return;

      const nx = Math.min(1, Math.max(0, normX));
      const displayNy = Math.min(1, Math.max(0, normY));

      const serverNy = adjustNormYForPtzAim(displayNy);
      let z = Math.round(ptzRef.current.zoom);
      const limits = ptzLimitsRef.current;
      if (limits) {
        z = Math.round(
          Math.min(limits.zoom.max, Math.max(limits.zoom.min, z)),
        );
      } else {
        z = Math.min(25, Math.max(1, z));
      }

      void ptzFocusFromRing(cameraId, {
        normX: nx,
        normY: serverNy,
        zoom: z,
      })
        .then((s) => control.syncPtz(s))
        .catch((err) => {
          console.warn("Patrol PTZ focus from panorama pick failed:", err);
        });
    },
    [cameraId, control, enabled],
  );

  return {
    ...control,
    vendor,
    usesPresetGridPanoramaPick,
    cameraPresets,
    mappedPresetGridIds,
    selectedPresetGridId,
    handlePresetGridSelect,
    applyPanoramaAimFromNorm,
    ptzOperable: control.ready,
  };
}
